import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { MRT_ColumnDef, MRT_Row, MRT_SortingState, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import { Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import { LegalEntityAddressDto } from 'src/generated/services/TFinancialApi';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';

import { useDeleteLegalEntityAddresses, useUpsertLegalEntityAddress } from 'src/use-cases/settings/useLegalEntities';
import { SettingsTableMain } from 'src/presentations/components/molecules/settings/table/TableMain';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';
import { AddressType } from 'src/types';

interface LegalEntityAddressTableProps {
  addresses: LegalEntityAddressDto[];
  onEditAddress?: (address: LegalEntityAddressDto) => void;
  legalEntityId: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const LegalEntityAddressTable = forwardRef<any, LegalEntityAddressTableProps>(({ addresses, onEditAddress, legalEntityId }, ref) => {
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [rowToDelete, setRowToDelete] = useState<LegalEntityAddressDto | null>(null);
  // Default sort by ID in ascending order
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: 'id',
      desc: false,
    },
  ]);
  const { mutateAsync: upsertAddress } = useUpsertLegalEntityAddress();
  const { mutateAsync: deleteAddresses, isLoading: isDeleting } = useDeleteLegalEntityAddresses();

  // Beautify enum values for display
  const getAddressTypeLabel = (type: string | null | undefined): string => {
    if (!type) return '';
    // Convert from SNAKE_CASE to Title Case with spaces
    return type.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
  };

  // Get all address types as options
  const addressTypeOptions = Object.values(AddressType).map((type) => ({
    value: type,
    label: getAddressTypeLabel(type),
  }));

  // Validate address fields
  const onValidateAddress = useCallback((address: Record<string, any>) => {
    const validationResult = {
      address: !address.address || address.address === '' ? 'Required' : '',
      country: !address.country || address.country === '' ? 'Required' : '',
      city: !address.city || address.city === '' ? 'Required' : '',
      zipcode: !address.zipcode || address.zipcode === '' ? 'Required' : '',
      type: !address.type ? 'Required' : '',
    };

    return validationResult;
  }, []);

  // CREATE action
  const handleCreateAddress: MRT_TableOptions<LegalEntityAddressDto>['onCreatingRowSave'] = async ({ values, exitCreatingMode }) => {
    const newValidationErrors = onValidateAddress(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      const reqBody: LegalEntityAddressDto = {
        legalEntityId,
        address: values.address,
        city: values.city,
        state: values.state,
        zipcode: values.zipcode,
        country: values.country || 'SG',
        type: values.type,
        active: values.active,
      };

      await upsertAddress(reqBody);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Address created successfully!',
      });
    } catch (error) {
      openToastError(new Error('Failed to create address'));
    } finally {
      exitCreatingMode();
    }
  };

  // UPDATE action
  const handleUpdateAddress: MRT_TableOptions<LegalEntityAddressDto>['onEditingRowSave'] = async ({ row, values, exitEditingMode }) => {
    const newValidationErrors = onValidateAddress(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      // Clean up values - convert '-' to null
      Object.keys(values).forEach((key) => {
        if (values[key] === '-') {
          values[key] = null;
        }
      });

      const reqBody: LegalEntityAddressDto = {
        id: row.original.id,
        legalEntityId: row.original.legalEntityId ?? legalEntityId,
        address: values.address,
        city: values.city,
        state: values.state,
        zipcode: values.zipcode,
        country: values.country || row.original.country || 'SG',
        type: values.type,
        active: row?.original?.active,
      };

      await upsertAddress(reqBody);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Address updated successfully!',
      });
    } catch (error) {
      openToastError(new Error('Failed to update address'));
    } finally {
      exitEditingMode();
    }
  };

  // DELETE action
  const handleDeleteAddress = async (address: LegalEntityAddressDto) => {
    if (!address.id) {
      openToast({
        toastType: 'error',
        title: 'Error',
        description: 'Cannot delete address: missing ID',
      });
      return;
    }

    try {
      await deleteAddresses([address.id]);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Address deleted successfully!',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    }
  };

  // Define the renderRowActions function separately with proper typing
  const renderRowActions = useCallback(
    ({ row, table }: { row: MRT_Row<LegalEntityAddressDto>; table: any }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    []
  );

  // Define columns for the Address table
  const addressColumns = useMemo<MRT_ColumnDef<LegalEntityAddressDto>[]>(
    () => [
      {
        enableEditing: false,
        accessorKey: 'id',
        header: 'ID',
      },
      {
        accessorKey: 'address',
        header: 'Address',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.address,
          helperText: validationErrors?.address,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              address: undefined,
            }),
        },
      },
      {
        accessorKey: 'country',
        header: 'Country',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.country,
          helperText: validationErrors?.country,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              country: undefined,
            }),
        },
      },
      {
        accessorKey: 'city',
        header: 'City',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.city,
          helperText: validationErrors?.city,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              city: undefined,
            }),
        },
      },
      {
        accessorKey: 'state',
        header: 'State',
        muiEditTextFieldProps: {
          error: !!validationErrors?.state,
          helperText: validationErrors?.state,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              state: undefined,
            }),
        },
      },
      {
        accessorKey: 'zipcode',
        header: 'Zipcode',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.zipcode,
          helperText: validationErrors?.zipcode,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              zipcode: undefined,
            }),
        },
      },
      {
        accessorKey: 'type',
        header: 'Address Type',
        Cell: ({ cell }) => getAddressTypeLabel(cell.getValue<string>()),
        muiEditTextFieldProps: {
          required: true,
          select: true,
          error: !!validationErrors?.type,
          helperText: validationErrors?.type,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              type: undefined,
            }),
          children: addressTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          )),
        },
      },
    ],
    [validationErrors]
  );

  // Configure the address table
  const addressTable = useMaterialReactTable({
    columns: addressColumns,
    data: addresses || [],
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableMultiSort: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    positionActionsColumn: 'first',
    enableEditing: true,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateAddress,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleUpdateAddress,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#F9FAFB',
        fontWeight: 'bold',
        '& .Mui-active': {
          color: '#00A651',
        },
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 80,
        muiTableHeadCellProps: {
          align: 'center',
        },
      },
    },
    renderEmptyRowsFallback: () => <p className='text-center py-4'>No addresses found</p>,
    renderRowActions,
  });

  // Expose table methods to parent component
  useImperativeHandle(ref, () => ({
    setCreatingRow: addressTable.setCreatingRow,
    getState: addressTable.getState,
  }));

  return (
    <div className='overflow-x-auto'>
      <SettingsTableMain table={addressTable} />

      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isDeleting}
        onAccept={async () => {
          if (rowToDelete) {
            await handleDeleteAddress(rowToDelete);
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure you want to delete this address?</p>
      </ModalConfirm>
    </div>
  );
});

// Set display name for devtools
LegalEntityAddressTable.displayName = 'LegalEntityAddressTable';
